
import { useState, useEffect } from "react";
import { commentsClient, type Comment } from "@/utils/commentsClient";
import { useAccount } from "wagmi";
import { toast } from "@/hooks/use-toast";

export function useComments(coinId: string, initialComments: Comment[] = []) {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [isLoading, setIsLoading] = useState(false);
  const { address } = useAccount();

  // Fetch comments when component mounts or coinId changes
  useEffect(() => {
    const fetchComments = async () => {
      setIsLoading(true);
      try {
        const fetchedComments = await commentsClient.fetchComments(coinId, address);
        setComments(fetchedComments);
      } catch (error) {
        console.error("Error fetching comments:", error);
        toast({
          title: "Error",
          description: "Failed to load comments",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [coinId, address]);

  const addComment = async (content: string) => {
    setIsLoading(true);
    try {
      const newComment = await commentsClient.postComment(content, coinId, address);
      if (newComment) {
        setComments((prev) => [newComment, ...prev]);
        toast({
          title: "Success",
          description: "Comment posted successfully",
        });
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      toast({
        title: "Error",
        description: "Failed to post comment",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addReply = async (content: string, commentId: number | string) => {
    setIsLoading(true);
    try {
      const newReply = await commentsClient.postReply(content, commentId, coinId, address);
      if (newReply) {
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id === commentId) {
              return {
                ...comment,
                replies: [
                  ...(comment.replies || []),
                  newReply,
                ],
              };
            }
            return comment;
          })
        );
        toast({
          title: "Success",
          description: "Reply posted successfully",
        });
      }
    } catch (error) {
      console.error("Error posting reply:", error);
      toast({
        title: "Error",
        description: "Failed to post reply",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLike = async (commentId: number | string) => {
    try {
      const result = await commentsClient.toggleLike(commentId, address);
      
      if (result.success) {
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id === commentId) {
              return {
                ...comment,
                likes: result.liked ? comment.likes + 1 : comment.likes - 1,
                liked_by_user: result.liked,
              };
            }
            
            // Check if the comment is in replies
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map((reply) => {
                  if (reply.id === commentId) {
                    return {
                      ...reply,
                      likes: result.liked ? reply.likes + 1 : reply.likes - 1,
                      liked_by_user: result.liked,
                    };
                  }
                  return reply;
                }),
              };
            }
            
            return comment;
          })
        );
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      toast({
        title: "Error",
        description: "Failed to process like",
        variant: "destructive",
      });
    }
  };

  return {
    comments,
    isLoading,
    addComment,
    addReply,
    toggleLike,
  };
}
